// Phone helpers for the sign-in / sign-up code (OTP) flows.
//
// These follow the same rules as the server (send-registration-otp /
// send-login-otp): a number only gets +1 when it is unambiguously a US/Canada
// number. Anything else has to start with + and a country code. We never
// guess: forcing +1 onto a foreign number (or truncating it to 10 digits) made
// a real-looking US number and texted the sign-in code to a stranger.

export const PHONE_FORMAT_ERROR =
  'Enter a 10-digit US number, or include your country code starting with +.';
export const PHONE_HELP_TEXT =
  'US numbers: 10 digits. Outside the US: start with + and your country code.';
export const OTP_SEND_FAILED = "We couldn't send the code. Please try again.";

/**
 * Normalise what the user typed to E.164, or null when it can't be done
 * without guessing.
 *  - starts with "+"            -> "+" and the digits (8-15 digits)
 *  - exactly 10 digits          -> "+1" and the digits (US/Canada)
 *  - 11 digits starting with 1  -> "+" and the digits (US/Canada)
 */
export function toE164(input: string): string | null {
  const raw = (input || '').trim();
  const digits = raw.replace(/\D/g, '');
  if (raw.startsWith('+')) {
    return digits.length >= 8 && digits.length <= 15 ? `+${digits}` : null;
  }
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`;
  return null;
}

function formatUsDigits(digits: string): string {
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

/**
 * Format a phone field as the user types. International numbers (leading +)
 * are kept as "+" and digits; everything else is shown in US style. Digits are
 * never dropped: a number that is too long is shown as typed (and toE164
 * rejects it), instead of being cut down to a different, real number.
 */
export function formatPhoneInput(value: string): string {
  const raw = (value || '').trimStart();
  const digits = raw.replace(/\D/g, '');
  if (raw.startsWith('+')) return `+${digits}`;
  if (digits.length === 11 && digits.startsWith('1')) return `1 ${formatUsDigits(digits.slice(1))}`;
  if (digits.length > 10) return digits;
  return formatUsDigits(digits);
}

function bodyError(body: unknown): string | null {
  const msg = (body as { error?: unknown } | null | undefined)?.error;
  return typeof msg === 'string' && msg.trim() ? msg : null;
}

/**
 * The message to show when sending (or verifying) a code failed. Reads the
 * server's `error` from a `supabase.functions.invoke` failure
 * (FunctionsHttpError keeps the response on `error.context`) or from a
 * returned `{ error }` body, and falls back to a generic message rather than a
 * technical one.
 */
export async function otpErrorMessage(
  error: unknown,
  data?: unknown,
  fallback: string = OTP_SEND_FAILED
): Promise<string> {
  const ctx = (error as { context?: unknown } | null | undefined)?.context as Response | undefined;
  if (ctx && typeof ctx.clone === 'function') {
    try {
      const text = await ctx.clone().text();
      const fromResponse = bodyError(JSON.parse(text));
      if (fromResponse) return fromResponse;
    } catch {
      // Body missing or not JSON: fall through.
    }
  }
  return bodyError(data) || fallback;
}
